// Frontend helpers per URL Cloudinary con trasformazioni.
// Il cloud_name viene fornito dal backend via /api/meta/options (`cloudinary_cloud_name`).

/**
 * Ritorna true se `url` è un URL Cloudinary (secure_url o public_id-based).
 */
export const isCloudinaryUrl = (url) => typeof url === 'string' && url.startsWith('https://res.cloudinary.com/');

/**
 * Estrae il public_id da un URL Cloudinary. Supporta URL con o senza trasformazioni.
 * Esempio: https://res.cloudinary.com/i4efi20t/image/upload/v1234/scintilla-hub/xxx.pdf
 *   → "scintilla-hub/xxx"
 */
export const extractPublicId = (url) => {
  if (!isCloudinaryUrl(url)) return '';
  try {
    const marker = '/upload/';
    const i = url.indexOf(marker);
    if (i < 0) return '';
    let rest = url.substring(i + marker.length);
    // Rimuovi trasformazioni (segmenti che contengono "_" tipo w_300,f_auto)
    const firstSlash = rest.indexOf('/');
    if (firstSlash > 0 && /[a-z]_/.test(rest.substring(0, firstSlash))) {
      rest = rest.substring(firstSlash + 1);
    }
    // Rimuovi eventuale "v1234/" (version)
    rest = rest.replace(/^v\d+\//, '');
    // Rimuovi estensione finale
    return rest.replace(/\.[a-zA-Z0-9]+$/, '');
  } catch (e) {
    return '';
  }
};

/**
 * Anteprima della prima pagina di un PDF caricato su Cloudinary come immagine JPEG ottimizzata.
 * @param {string} cloudName Il cloud name di Cloudinary
 * @param {string} publicId  public_id del file (o URL Cloudinary da cui estrarre)
 * @param {object} opts      { page=1, width=300 }
 */
export const cloudinaryPdfPreview = (cloudName, publicId, { page = 1, width = 300 } = {}) => {
  if (!cloudName) return '';
  if (!publicId) return '';
  const id = isCloudinaryUrl(publicId) ? extractPublicId(publicId) : publicId;
  if (!id) return '';
  const tx = `pg_${Math.max(1, page)},w_${Math.max(50, width)},c_limit,f_auto,q_auto`;
  return `https://res.cloudinary.com/${cloudName}/image/upload/${tx}/${id}.jpg`;
};

/**
 * URL immagine ottimizzata (f_auto,q_auto) — riduce banda su mobile e usa il formato migliore per il browser.
 * @param {string} cloudName
 * @param {string} publicIdOrUrl
 * @param {object} opts { width }
 */
export const cloudinaryOptimizedImage = (cloudName, publicIdOrUrl, { width } = {}) => {
  if (!cloudName || !publicIdOrUrl) return publicIdOrUrl || '';
  // Se non è un URL Cloudinary, ritorna così com'è (es. base64 legacy)
  if (!isCloudinaryUrl(publicIdOrUrl) && !/^[\w\-/]+$/.test(publicIdOrUrl)) return publicIdOrUrl;
  const id = isCloudinaryUrl(publicIdOrUrl) ? extractPublicId(publicIdOrUrl) : publicIdOrUrl;
  const parts = ['f_auto', 'q_auto'];
  if (width) { parts.push(`w_${Math.round(width)}`); parts.push('c_limit'); }
  const tx = parts.join(',');
  return `https://res.cloudinary.com/${cloudName}/image/upload/${tx}/${id}`;
};
